// src/components/Supplier/SupplierTable.jsx
// Component to display supplier data in a table format

import React from 'react';
import RoleBasedComponent from '../common/RoleBasedComponent';
/**
 * SupplierTable Component
 * Displays a list of suppliers in a table format with edit and delete actions
 * 
 * @param {Array} suppliers - Array of supplier objects to display
 * @param {Function} onEdit - Callback function when edit button is clicked
 * @param {Function} onDelete - Callback function when delete button is clicked
 * @param {boolean} isLoading - Whether data is currently loading
 */
const SupplierTable = ({ suppliers, onEdit, onDelete, isLoading }) => {
  /**
   * Handle edit button click
   * @param {Object} supplier - The supplier item to edit
   */
  const handleEdit = (supplier) => {
    if (onEdit) {
      onEdit(supplier);
    }
  };

  /**
   * Handle delete button click
   * @param {Object} supplier - The supplier item to delete
   */
  const handleDelete = (supplier) => {
    if (onDelete) {
      onDelete(supplier);
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="inventory-data-table-container">
        <table className="inventory-data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Supplier Name</th>
              <th>Contact</th>
              <th>Address</th>
              <th>Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan="6" className="inventory-loading">
                Loading supplier data...
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  // Show empty state
  if (!suppliers || suppliers.length === 0) {
    return (
      <div className="inventory-data-table-container">
        <table className="inventory-data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Supplier Name</th>
              <th>Contact</th>
              <th>Address</th>
              <th>Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan="6" className="inventory-empty-state">
                No suppliers match the current filter.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  // Render table with data
  return (
    <div className="inventory-data-table-container">
      <table className="inventory-data-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Supplier Name</th>
            <th>Contact</th>
            <th>Address</th>
            <th>Active</th>
            <th>Actions</th>
          </tr>
        </thead>

        <tbody>
          {suppliers.map((supplier) => (
            <tr key={supplier.id || supplier.supplierId}>
              {/* Supplier ID */}
              <td>{supplier.supplierId || supplier.id}</td>

              {/* Supplier Name */}
              <td>{supplier.name}</td>

              {/* Contact */}
              <td>{supplier.contact || '-'}</td>

              {/* Address */}
              <td>{supplier.address || '-'}</td>

              {/* Active */}
              <td>
                <span className={`category-status-badge ${
                  supplier.isActive ? 'category-status-active' : 'category-status-inactive'
                }`}>
                  {supplier.isActive ? (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '4px' }}>
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                      </svg>
                      Active
                    </>
                  ) : (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '4px' }}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="15" y1="9" x2="9" y2="15"></line>
                        <line x1="9" y1="9" x2="15" y2="15"></line>
                      </svg>
                      Inactive
                    </>
                  )}
                </span>
              </td>

              {/* Actions */}
              <td>
                <button
  className="inventory-btn inventory-btn-action inventory-btn-edit"
  onClick={() => handleEdit(supplier)}
  title="Edit this supplier"
>
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
  Edit
</button>

<RoleBasedComponent allowedRoles={['superadmin']}>
  <button
    className="inventory-btn inventory-btn-action inventory-btn-delete"
    onClick={() => handleDelete(supplier)}
    title="Delete this supplier"
    style={{ marginLeft: '8px' }}
  >
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="3 6 5 6 21 6"></polyline>
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
      <line x1="10" y1="11" x2="10" y2="17"></line>
      <line x1="14" y1="11" x2="14" y2="17"></line>
    </svg>
    Delete
  </button>
</RoleBasedComponent>

              </td>

            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default SupplierTable;
